// web/src/screens/config.ts — Phase 5 UI-03 + multi-voice TTS picker.
//
// /config screen: name/age/sex persisted to the singleton app_user row via
// /api/config/profile (these feed the {{PlayerName}}/{{PlayerAge}}/
// {{PlayerSex}} tokens the turn handler expands into a game's system
// prompt); STT/LLM provider pickers (still disabled — Hugo.lv MVP-2); and
// a real TTS voice picker backed by /api/config/tts. Changing the voice
// persists to Postgres AND atomically swaps the runtime audio.Resolver —
// the new voice is in effect immediately, no rebuild or restart required,
// provided the voice has been pre-rendered (otherwise the backend returns
// 422 voice_not_rendered and the picker shows the option as disabled with
// the suffix "— vēl nav sagatavota").
import { navigate } from "../main.js";
import {
  getTTSConfig,
  setTTSChoice,
  getTurnTimeouts,
  setTurnTimeouts,
  getProfile,
  setProfile,
  TTSConfigError,
} from "../api/config.js";
import { type TTSConfig, type TurnTimeouts, type ChildProfile } from "../types/api.js";
import {
  readFinalScreenMode,
  writeFinalScreenMode,
  type FinalScreenMode,
} from "./final.js";
import {
  readMicBeepOverride,
  writeMicBeepOverride,
  resolveMicBeepEnabled,
} from "./mic_beep_pref.js";

const TIMEOUT_MIN_MS = 1000;
const TIMEOUT_MAX_MS = 60000;

export function mount(root: HTMLElement, _props?: unknown): () => void {
  root.innerHTML = `
    <section class="screen-config" style="
      min-height: 100dvh;
      padding: var(--space-lg);
      background: var(--surface);
      display: flex; flex-direction: column;
      align-items: center; gap: var(--space-lg);
    ">
      <div style="width: 100%; max-width: 480px;">
        <button class="back-link" style="
          background: none; border: 0;
          font-size: var(--type-body); color: var(--text-muted);
          padding: var(--space-sm) var(--space-md);
          min-height: 44px; cursor: pointer;
        " aria-label="Atpakaļ">← Atpakaļ</button>
      </div>
      <h1 style="
        font-size: var(--type-heading); font-weight: 600;
        text-align: center; margin: 0;
      ">Iestatījumi</h1>

      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Vārds:</span>
        <input type="text" class="cfg-name" style="
          font-size: var(--type-body); min-height: 64px;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        " />
      </label>
      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Vecums:</span>
        <input type="number" class="cfg-age" min="2" max="10" style="
          font-size: var(--type-body); min-height: 64px;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        " />
      </label>
      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Dzimums:</span>
        <select class="cfg-sex" style="
          font-size: var(--type-body); min-height: 64px;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        ">
          <option value="">—</option>
          <option value="m">Zēns</option>
          <option value="f">Meitene</option>
          <option value="other">Cits</option>
        </select>
      </label>

      <p class="cfg-profile-status" aria-live="polite" style="
        font-size: 0.875em; color: var(--text-muted);
        max-width: 480px; width: 100%; margin: 0; min-height: 1.5em;
      "></p>

      <h2 style="
        font-size: var(--type-body); font-weight: 600;
        color: var(--text-muted); margin: var(--space-md) 0 0 0;
        max-width: 480px; width: 100%;
      ">Pakalpojumu sniedzēji</h2>
      <select aria-disabled="true" disabled title="Drīzāk — Hugo.lv (MVP-2)" style="
        font-size: var(--type-body); min-height: 64px;
        opacity: 0.55; cursor: not-allowed; width: 100%; max-width: 480px;
        border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
        padding: var(--space-sm) var(--space-md);
      "><option>OpenAI (STT)</option></select>
      <select aria-disabled="true" disabled title="Drīzāk — Hugo.lv (MVP-2)" style="
        font-size: var(--type-body); min-height: 64px;
        opacity: 0.55; cursor: not-allowed; width: 100%; max-width: 480px;
        border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
        padding: var(--space-sm) var(--space-md);
      "><option>OpenAI (LLM)</option></select>

      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Balss (TTS):</span>
        <select class="cfg-tts-voice" style="
          font-size: var(--type-body); min-height: 64px;
          width: 100%;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        "></select>
      </label>

      <p class="cfg-tts-hint" style="
        font-size: 0.875em; color: var(--text-muted);
        max-width: 480px; width: 100%; margin: 0;
      ">Esošo balsu maiņa stājas spēkā uzreiz. Lai pievienotu jaunu balsi, palaid <code>make ttsbuild</code>.</p>
      <p class="cfg-tts-status" aria-live="polite" style="
        font-size: 0.875em; color: var(--text-muted);
        max-width: 480px; width: 100%; margin: 0; min-height: 1.5em;
      "></p>

      <h2 style="
        font-size: var(--type-body); font-weight: 600;
        color: var(--text-muted); margin: var(--space-md) 0 0 0;
        max-width: 480px; width: 100%;
      ">Klausīšanās laiki (sekundes)</h2>

      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Mikrofons klausās (s):</span>
        <input type="number" class="cfg-mic-listen" min="1" max="60" step="1" style="
          font-size: var(--type-body); min-height: 64px;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        " />
      </label>

      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">STT laika limits (s):</span>
        <input type="number" class="cfg-stt-timeout" min="1" max="60" step="1" style="
          font-size: var(--type-body); min-height: 64px;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        " />
      </label>

      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Pauze pēc burta atklāšanas (s):</span>
        <input type="number" class="cfg-reveal-settle" min="0" max="30" step="0.5" style="
          font-size: var(--type-body); min-height: 64px;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        " />
      </label>

      <p class="cfg-timeouts-status" aria-live="polite" style="
        font-size: 0.875em; color: var(--text-muted);
        max-width: 480px; width: 100%; margin: 0; min-height: 1.5em;
      "></p>

      <h2 style="
        font-size: var(--type-body); font-weight: 600;
        color: var(--text-muted); margin: var(--space-md) 0 0 0;
        max-width: 480px; width: 100%;
      ">Mikrofons</h2>

      <label style="display: flex; align-items: center; gap: var(--space-sm); width: 100%; max-width: 480px; min-height: 44px;">
        <input type="checkbox" class="cfg-mic-beep" style="
          width: 22px; height: 22px;
        " />
        <span style="font-size: var(--type-body);">Atskaņot signālu, kad ieslēdzas mikrofons</span>
      </label>

      <h2 style="
        font-size: var(--type-body); font-weight: 600;
        color: var(--text-muted); margin: var(--space-md) 0 0 0;
        max-width: 480px; width: 100%;
      ">Pēdējais ekrāns</h2>

      <label style="display: flex; flex-direction: column; gap: var(--space-xs); width: 100%; max-width: 480px;">
        <span style="font-size: var(--type-body);">Otrā poga:</span>
        <select class="cfg-final-mode" style="
          font-size: var(--type-body); min-height: 64px;
          width: 100%;
          border: 2px solid var(--theme-color, #5E3FBE); border-radius: 8px;
          padding: var(--space-sm) var(--space-md);
        ">
          <option value="next">Nākamā spēle (+ saite uz sarakstu)</option>
          <option value="other">Cita spēle</option>
        </select>
      </label>

      <a href="/privacy" class="privacy-link" style="
        font-size: var(--type-body); color: var(--theme-color, #5E3FBE);
        margin-top: var(--space-md); min-height: 44px;
        display: flex; align-items: center;
      ">Privātums →</a>
    </section>
  `;

  const back = root.querySelector(".back-link") as HTMLButtonElement;
  const nameEl = root.querySelector(".cfg-name") as HTMLInputElement;
  const ageEl = root.querySelector(".cfg-age") as HTMLInputElement;
  const sexEl = root.querySelector(".cfg-sex") as HTMLSelectElement;
  const profileStatusEl = root.querySelector(".cfg-profile-status") as HTMLParagraphElement;
  const ttsVoiceEl = root.querySelector(".cfg-tts-voice") as HTMLSelectElement;
  const statusEl = root.querySelector(".cfg-tts-status") as HTMLParagraphElement;
  const micListenEl = root.querySelector(".cfg-mic-listen") as HTMLInputElement;
  const sttTimeoutEl = root.querySelector(".cfg-stt-timeout") as HTMLInputElement;
  const revealSettleEl = root.querySelector(".cfg-reveal-settle") as HTMLInputElement;
  const timeoutsStatusEl = root.querySelector(".cfg-timeouts-status") as HTMLParagraphElement;
  const finalModeEl = root.querySelector(".cfg-final-mode") as HTMLSelectElement;
  const micBeepEl = root.querySelector(".cfg-mic-beep") as HTMLInputElement;
  const privacyLink = root.querySelector(".privacy-link") as HTMLAnchorElement;

  // Final-screen mode is a localStorage-only demo preference (no backend).
  finalModeEl.value = readFinalScreenMode();
  function onFinalModeChange(): void {
    writeFinalScreenMode(finalModeEl.value as FinalScreenMode);
  }
  finalModeEl.addEventListener("change", onFinalModeChange);

  // Mic-open beep — per-device override on top of the admin-set default.
  // Initial render seeds the checkbox with the resolved effective value
  // (override-or-default) so the user sees what's actually in effect; any
  // toggle then writes a localStorage override that persists per device.
  void (async () => {
    if (readMicBeepOverride() === null) {
      micBeepEl.checked = await resolveMicBeepEnabled();
    } else {
      micBeepEl.checked = readMicBeepOverride() === "on";
    }
  })();
  function onMicBeepChange(): void {
    writeMicBeepOverride(micBeepEl.checked ? "on" : "off");
  }
  micBeepEl.addEventListener("change", onMicBeepChange);

  // Player profile (name/age/sex) — persisted to the singleton app_user row
  // via /api/config/profile. Loaded once on mount; each input `change`
  // writes the whole profile back (the server trims/validates and echoes the
  // saved row, which we re-render). These three fields feed the
  // {{PlayerName}}/{{PlayerAge}}/{{PlayerSex}} prompt tokens on the next turn.
  function setProfileStatus(text: string, isError = false): void {
    profileStatusEl.textContent = text;
    profileStatusEl.style.color = isError
      ? "var(--theme-color, #c62828)"
      : "var(--text-muted)";
  }

  function renderProfile(p: ChildProfile): void {
    nameEl.value = p.name;
    ageEl.value = p.age != null ? String(p.age) : "";
    sexEl.value = p.sex;
  }

  async function loadProfile(): Promise<void> {
    try {
      const p = await getProfile();
      if (abortLoad) return;
      renderProfile(p);
    } catch (err) {
      if (abortLoad) return;
      setProfileStatus(`Neizdevās ielādēt profilu: ${(err as Error).message}`, true);
    }
  }

  async function persistProfile(): Promise<void> {
    const ageRaw = ageEl.value.trim();
    let age: number | null = null;
    if (ageRaw !== "") {
      age = parseInt(ageRaw, 10);
      if (!Number.isFinite(age) || age < 2 || age > 10) {
        setProfileStatus("Vecumam jābūt no 2 līdz 10", true);
        return;
      }
    }
    setProfileStatus("Saglabā…");
    try {
      const saved = await setProfile({ name: nameEl.value.trim(), age, sex: sexEl.value });
      renderProfile(saved);
      setProfileStatus("Saglabāts.");
    } catch (err) {
      setProfileStatus(`Neizdevās saglabāt: ${(err as Error).message}`, true);
    }
  }

  // Encode option key as `${provider}|${voice}` so a single string round-trips
  // through the <option value> without needing to look up by index.
  function optionKey(provider: string, voice: string): string {
    return `${provider}|${voice}`;
  }

  function renderTTSConfig(cfg: TTSConfig): void {
    ttsVoiceEl.innerHTML = "";
    for (const opt of cfg.options) {
      const o = document.createElement("option");
      o.value = optionKey(opt.provider, opt.voice);
      if (opt.available) {
        o.textContent = opt.label;
      } else {
        o.textContent = `${opt.label} — vēl nav sagatavota`;
        o.disabled = true;
      }
      ttsVoiceEl.appendChild(o);
    }
    ttsVoiceEl.value = optionKey(cfg.provider, cfg.voice);
  }

  function setStatus(text: string, isError = false): void {
    statusEl.textContent = text;
    statusEl.style.color = isError
      ? "var(--theme-color, #c62828)"
      : "var(--text-muted)";
  }

  function setTimeoutsStatus(text: string, isError = false): void {
    timeoutsStatusEl.textContent = text;
    timeoutsStatusEl.style.color = isError
      ? "var(--theme-color, #c62828)"
      : "var(--text-muted)";
  }

  function renderTurnTimeouts(t: TurnTimeouts): void {
    micListenEl.value = String(Math.round(t.mic_listen_ms / 1000));
    sttTimeoutEl.value = String(Math.round(t.stt_timeout_ms / 1000));
    // Reveal settle is sub-second-meaningful (e.g. 0.5s steps), so keep one
    // decimal place rather than rounding to whole seconds.
    revealSettleEl.value = String(t.missing_letter_reveal_settle_ms / 1000);
  }

  async function loadTurnTimeouts(): Promise<void> {
    try {
      const t = await getTurnTimeouts();
      if (abortLoad) return;
      renderTurnTimeouts(t);
    } catch (err) {
      if (abortLoad) return;
      setTimeoutsStatus(`Neizdevās ielādēt laikus: ${(err as Error).message}`, true);
    }
  }

  async function persistTimeouts(): Promise<void> {
    const micSec = parseInt(micListenEl.value, 10);
    const sttSec = parseInt(sttTimeoutEl.value, 10);
    const revealSec = parseFloat(revealSettleEl.value);
    if (!Number.isFinite(micSec) || !Number.isFinite(sttSec) || !Number.isFinite(revealSec)) {
      setTimeoutsStatus("Ievadi skaitļus", true);
      return;
    }
    const micMs = micSec * 1000;
    const sttMs = sttSec * 1000;
    const revealMs = Math.round(revealSec * 1000);
    if (micMs < TIMEOUT_MIN_MS || micMs > TIMEOUT_MAX_MS ||
        sttMs < TIMEOUT_MIN_MS || sttMs > TIMEOUT_MAX_MS) {
      setTimeoutsStatus(`Vērtībai jābūt no ${TIMEOUT_MIN_MS / 1000} līdz ${TIMEOUT_MAX_MS / 1000} s`, true);
      return;
    }
    if (revealMs < 0 || revealMs > 30000) {
      setTimeoutsStatus("Pauzei pēc atklāšanas jābūt no 0 līdz 30 s", true);
      return;
    }
    setTimeoutsStatus("Saglabā…");
    try {
      const saved = await setTurnTimeouts({
        mic_listen_ms: micMs,
        stt_timeout_ms: sttMs,
        missing_letter_reveal_settle_ms: revealMs,
      });
      renderTurnTimeouts(saved);
      setTimeoutsStatus("Saglabāts.");
    } catch (err) {
      setTimeoutsStatus(`Neizdevās saglabāt: ${(err as Error).message}`, true);
    }
  }

  let abortLoad = false;
  async function loadTTSConfig(): Promise<void> {
    try {
      const cfg = await getTTSConfig();
      if (abortLoad) return;
      renderTTSConfig(cfg);
    } catch (err) {
      if (abortLoad) return;
      setStatus(`Neizdevās ielādēt TTS iestatījumus: ${(err as Error).message}`, true);
    }
  }

  async function onVoiceChange(): Promise<void> {
    const [provider, voice] = ttsVoiceEl.value.split("|");
    if (!provider || !voice) return;
    setStatus("Saglabā…");
    try {
      const cfg = await setTTSChoice(provider, voice);
      renderTTSConfig(cfg);
      setStatus("Balss nomainīta.");
    } catch (err) {
      if (err instanceof TTSConfigError && err.code === "voice_not_rendered") {
        setStatus("Šī balss vēl nav sagatavota. Palaid `make ttsbuild` un restartē serveri.", true);
        // Reload to refresh availability state and revert the picker.
        void loadTTSConfig();
        return;
      }
      setStatus(`Neizdevās saglabāt: ${(err as Error).message}`, true);
    }
  }

  function onBack(): void { history.back(); }
  function onPrivacyClick(e: Event): void { e.preventDefault(); navigate("/privacy"); }

  back.addEventListener("click", onBack);
  nameEl.addEventListener("change", persistProfile);
  ageEl.addEventListener("change", persistProfile);
  sexEl.addEventListener("change", persistProfile);
  ttsVoiceEl.addEventListener("change", onVoiceChange);
  micListenEl.addEventListener("change", persistTimeouts);
  sttTimeoutEl.addEventListener("change", persistTimeouts);
  revealSettleEl.addEventListener("change", persistTimeouts);
  privacyLink.addEventListener("click", onPrivacyClick);

  void loadProfile();
  void loadTTSConfig();
  void loadTurnTimeouts();

  return (): void => {
    abortLoad = true;
    back.removeEventListener("click", onBack);
    nameEl.removeEventListener("change", persistProfile);
    ageEl.removeEventListener("change", persistProfile);
    sexEl.removeEventListener("change", persistProfile);
    ttsVoiceEl.removeEventListener("change", onVoiceChange);
    micListenEl.removeEventListener("change", persistTimeouts);
    sttTimeoutEl.removeEventListener("change", persistTimeouts);
    revealSettleEl.removeEventListener("change", persistTimeouts);
    finalModeEl.removeEventListener("change", onFinalModeChange);
    micBeepEl.removeEventListener("change", onMicBeepChange);
    privacyLink.removeEventListener("click", onPrivacyClick);
  };
}
